/**
 * 언어 코드를 제거하는 유틸리티 함수
 * @param path - 언어 코드가 포함된 경로
 * @returns 언어 코드가 제거된 경로 e.g) /ko/signin -> /signin
 */
export const removeLocaleFromPath = (path: string | null): string | null => {
  if (!path) return null;
  if (/^\/[a-z]+(?:-[a-z]+)?\/?$/.test(path)) return '/'; // 언어 코드만 있는 경우

  // /ko/, /en/, /ko-kr/ 등의 패턴을 제거
  return path.replace(/^\/[a-z]+(?:-[a-z]+)?\//, '/');
};

/**
 * 지원하는 언어 코드 목록
 */
export const SUPPORTED_LOCALES = ['ko', 'en'] as const;

/**
 * 언어 코드 타입
 */
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * 언어 코드와 언어명 매핑
 */
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  ko: 'korean',
  en: 'english',
} as const;

/**
 * URL 경로에서 언어 코드를 추출하는 함수
 * @param path - URL 경로 (예: '/ko/output/197' 또는 window.location.pathname)
 * @returns 언어 코드 또는 null (언어 코드가 없는 경우)
 */
export const getLocaleFromPath = (path: string): SupportedLocale | null => {
  if (!path) return null;

  const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//);
  if (!match) return null;

  const locale = match[1] as SupportedLocale;
  return SUPPORTED_LOCALES.includes(locale) ? locale : null;
};

/**
 * 현재 URL에서 언어 코드를 가져오는 함수 (클라이언트 사이드)
 * @returns 현재 언어 코드 또는 기본값 'ko'
 */
export const getCurrentLocale = (): SupportedLocale => {
  if (typeof window === 'undefined') return 'ko'; // 서버 사이드에서는 기본값 반환

  const locale = getLocaleFromPath(window.location.pathname);
  return locale || 'ko'; // 언어 코드가 없으면 기본값 'ko' 반환
};

/**
 * 언어 코드를 언어명으로 변환하는 함수
 * @param locale - 언어 코드
 * @returns 언어명 (예: 'ko' -> 'korean')
 */
export const getLocaleName = (locale: SupportedLocale): string => {
  return LOCALE_NAMES[locale];
};

/**
 * 언어명을 언어 코드로 변환하는 함수
 * @param name - 언어명
 * @returns 언어 코드 또는 null (매칭되는 언어가 없는 경우)
 */
export const getLocaleByName = (name: string): SupportedLocale | null => {
  const entry = Object.entries(LOCALE_NAMES).find(
    ([, value]) => value === name.toLowerCase(),
  );
  return entry ? (entry[0] as SupportedLocale) : null;
};

/**
 * 지원하는 모든 언어 목록을 가져오는 함수
 * @returns 언어 코드와 언어명의 배열
 */
export const getAllLocales = (): Array<{
  code: SupportedLocale;
  name: string;
}> => {
  return SUPPORTED_LOCALES.map((code) => ({
    code,
    name: LOCALE_NAMES[code],
  }));
};
